import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { BILLING } from "@/constants/testIds";
import { Flame, Check, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const SQUARE_SDK_URL_SANDBOX = "https://sandbox.web.squarecdn.com/v1/square.js";
const SQUARE_SDK_URL_PROD = "https://web.squarecdn.com/v1/square.js";

const fmtCents = (c) => `$${(Number(c || 0) / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

function loadSquareSdk(env) {
  return new Promise((resolve, reject) => {
    if (window.Square) return resolve(window.Square);
    const s = document.createElement("script");
    s.src = env === "production" ? SQUARE_SDK_URL_PROD : SQUARE_SDK_URL_SANDBOX;
    s.onload = () => resolve(window.Square);
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

export default function StartTrial() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [selected, setSelected] = useState("online_monthly");
  const [loading, setLoading] = useState(false);
  const [paymentsReady, setPaymentsReady] = useState(false);
  const cardRef = useRef(null);
  const cardInstance = useRef(null);

  // If the user already has an active subscription/trial, skip this page.
  useEffect(() => {
    const status = user?.subscription?.status;
    if (status === "active" || status === "trial") {
      if (user.is_coach) navigate("/coach-dashboard", { replace: true });
      else if (!user.onboarded) navigate("/onboarding", { replace: true });
      else navigate("/dashboard", { replace: true });
    }
  }, [user, navigate]);

  // Pull Square public config from backend
  useEffect(() => {
    api.get("/billing/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  // Initialize Square card form
  useEffect(() => {
    if (!config || !cardRef.current) return;
    // Guard against StrictMode double-invoke / config-change double mount.
    if (cardInstance.current) return;
    let cancelled = false;
    const container = cardRef.current;
    (async () => {
      try {
        if (!config.square_app_id || !config.square_location_id) {
          throw new Error("Missing Square config from server");
        }
        const Square = await loadSquareSdk(config.environment);
        if (cancelled) return;
        const payments = Square.payments(config.square_app_id, config.square_location_id);
        const card = await payments.card();
        if (cancelled) {
          await card.destroy?.();
          return;
        }
        await card.attach(container);
        cardInstance.current = card;
        setPaymentsReady(true);
      } catch (e) {
        console.error("Square card form init failed:", e);
        toast.error("Could not load card form");
      }
    })();
    return () => {
      cancelled = true;
      const inst = cardInstance.current;
      cardInstance.current = null;
      inst?.destroy?.();
      // Clear any orphaned Square iframes from the container.
      if (container) container.innerHTML = "";
      setPaymentsReady(false);
    };
  }, [config]);

  const startTrial = async () => {
    if (!cardInstance.current) return toast.error("Card form not ready");
    setLoading(true);
    try {
      const result = await cardInstance.current.tokenize();
      if (result.status !== "OK") {
        toast.error("Card validation failed");
        setLoading(false);
        return;
      }
      await api.post("/billing/start-trial", {
        plan: selected,
        source_id: result.token,
        buyer_email: user?.email,
      });
      await refresh();
      toast.success(user?.is_coach ? "7-day free trial started!" : "Subscription active — welcome!");
      // Route to the right next step.
      if (user?.is_coach) navigate("/coach-dashboard");
      else navigate("/onboarding");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not complete signup");
    } finally {
      setLoading(false);
    }
  };

  const isCoach = !!user?.is_coach;

  // Build the plan list from the /billing/config response so pricing lives in
  // one place. Coaches keep the existing 2-option layout (they don't see
  // in-person tiers). Clients see grouped Online + In-Person cards.
  const clientPlans = config?.client_plans;
  const onlinePlans = clientPlans?.online || [];
  const inPersonPlans = clientPlans?.in_person || [];
  const allClientPlans = [...onlinePlans, ...inPersonPlans];
  const selectedPlan = allClientPlans.find((p) => p.plan === selected);
  const priceLabel = selectedPlan ? fmtCents(selectedPlan.amount_cents) : "";
  const isOneTime = selectedPlan && selectedPlan.recurring === false;

  // Coaches use the old 2-plan Starter monthly/annual pattern.
  const coachPlans = [
    { plan: "coach_starter_monthly", label: "Coach Monthly", price: fmtCents(config?.coach_monthly_price_cents), sub: "Cancel anytime" },
    { plan: "coach_starter_annual",  label: "Coach Annual",  price: fmtCents(config?.coach_annual_price_cents),  sub: "Save 20%" },
  ];

  // Ensure `selected` is compatible with the current user's plan pool.
  useEffect(() => {
    if (!config) return;
    if (isCoach) {
      if (!selected.startsWith("coach_")) setSelected("coach_starter_monthly");
    } else {
      if (!allClientPlans.some((p) => p.plan === selected)) setSelected("online_monthly");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, isCoach]);

  return (
    <div className="min-h-screen bg-obsidian-900 flex flex-col">
      <header className="border-b border-obsidian-600 px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img src="/icon-192.png" alt="Ripple Effect" className="h-6 w-6 rounded-sm" />
          <span className="font-heading text-xl">RIPPLE<span className="text-blaze"> EFFECT</span></span>
        </Link>
        <div className="text-[10px] text-zinc-500 uppercase tracking-widest">
          {isCoach ? "Step 2 of 2 — card on file" : "Step 2 of 2 — subscribe"}
        </div>
      </header>

      <main className="flex-1 grid place-items-center px-5 py-10">
        <div className="w-full max-w-xl obs-card" data-testid="start-trial-card">
          <div className="overline text-blaze mb-2">/ Almost in</div>
          <h1 className="font-heading text-4xl uppercase mb-2">
            {isCoach ? "7 days on us." : "Start your plan."}
          </h1>
          <p className="text-zinc-400 mb-6 text-sm">
            {isCoach ? (
              <>Pick a plan and add a card to start your <span className="text-zinc-100">free 7-day trial</span>. We won&apos;t charge anything today — cancel any time before day 7 in Settings.</>
            ) : isOneTime ? (
              <>Pick a plan and enter your card. You&apos;ll be charged <span className="text-zinc-100">{priceLabel} today</span> for the full package — no auto-renew. 24-hour money-back guarantee.</>
            ) : (
              <>Pick a plan and enter your card. You&apos;ll be charged <span className="text-zinc-100">{priceLabel} today</span> and your access starts immediately. Change your mind within 24 hours? Cancel in Settings for a full automatic refund.</>
            )}
          </p>

          {isCoach ? (
            <div className="grid grid-cols-2 gap-3 mb-5">
              {coachPlans.map((p) => (
                <button
                  key={p.plan}
                  data-testid={`trial-plan-${p.plan}`}
                  onClick={() => setSelected(p.plan)}
                  className={`text-left p-4 rounded-sm border transition-colors ${
                    selected === p.plan ? "border-blaze bg-blaze/10" : "border-obsidian-600 hover:border-zinc-500"
                  }`}
                >
                  <div className="overline">{p.label}</div>
                  <div className="font-heading text-2xl mt-1">{p.price}</div>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-widest mt-1">{p.sub}</div>
                </button>
              ))}
            </div>
          ) : (
            <div className="space-y-5 mb-5" data-testid="client-plan-picker">
              {onlinePlans.length > 0 && (
                <div>
                  <div className="overline text-zinc-500 mb-2">/ Online coaching (app + AI)</div>
                  <div className="grid grid-cols-2 gap-3">
                    {onlinePlans.map((p) => (
                      <button
                        key={p.plan}
                        type="button"
                        data-testid={`trial-plan-${p.plan}`}
                        onClick={() => setSelected(p.plan)}
                        className={`text-left p-3 rounded-sm border transition-colors ${
                          selected === p.plan ? "border-blaze bg-blaze/10" : "border-obsidian-600 hover:border-zinc-500"
                        }`}
                      >
                        <div className="overline">{p.label}</div>
                        <div className="font-heading text-xl mt-1">{fmtCents(p.amount_cents)}</div>
                        <div className="text-[10px] text-zinc-500 uppercase tracking-widest mt-0.5">{p.cadence}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {inPersonPlans.length > 0 && (
                <div>
                  <div className="overline text-zinc-500 mb-2">/ In-person training</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {inPersonPlans.map((p) => (
                      <button
                        key={p.plan}
                        type="button"
                        data-testid={`trial-plan-${p.plan}`}
                        onClick={() => setSelected(p.plan)}
                        className={`text-left p-3 rounded-sm border transition-colors ${
                          selected === p.plan ? "border-blaze bg-blaze/10" : "border-obsidian-600 hover:border-zinc-500"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="overline">{p.label}</div>
                          {p.recurring === false && (
                            <span className="text-[9px] text-emerald-300 uppercase tracking-widest border border-emerald-500/40 rounded-full px-1.5 py-0.5">
                              One-time
                            </span>
                          )}
                        </div>
                        <div className="font-heading text-xl mt-1">{fmtCents(p.amount_cents)}</div>
                        <div className="text-[10px] text-zinc-500 uppercase tracking-widest mt-0.5">{p.cadence}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <ul className="text-sm text-zinc-300 space-y-1 mb-5">
            {(isCoach
              ? [
                  "Unlimited AI coaching chat",
                  "Personalized macros + weekly meal plans",
                  "Auto-progression on every lift",
                  "Cancel any time in Settings before day 7",
                ]
              : [
                  "Unlimited AI coaching chat",
                  "Personalized macros + weekly meal plans",
                  "Auto-progression on every lift",
                  "24-hour money-back guarantee",
                ]
            ).map((l) => (
              <li key={l} className="flex gap-2"><Check className="h-3 w-3 text-blaze mt-1" /> {l}</li>
            ))}
          </ul>

          <div
            ref={cardRef}
            data-testid={BILLING.cardContainer}
            className="min-h-[60px] bg-obsidian-900 border border-obsidian-600 rounded-sm p-3 mb-4"
          />

          <button
            data-testid="start-trial-submit"
            onClick={startTrial}
            disabled={!paymentsReady || loading}
            className="blaze-btn w-full py-3 text-sm disabled:opacity-50 inline-flex items-center justify-center gap-2"
          >
            <Flame className="h-4 w-4" />
            {loading
              ? (isCoach ? "Starting…" : "Charging…")
              : (isCoach ? "Start 7-day free trial" : `Subscribe · charge ${priceLabel}`)}
          </button>
          <p className="text-[11px] text-zinc-500 mt-3 uppercase tracking-widest flex items-center gap-2">
            <ShieldCheck className="h-3 w-3" />
            {isCoach
              ? "Secure · Square · No charge for 7 days"
              : "Secure · Square · 24-hour money-back guarantee"}
          </p>
        </div>
      </main>
    </div>
  );
}
